import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Wand2, Play, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "../site/HeroSlides";

export function useVoices() {
  const [voices, setVoices] = useState(null);
  useEffect(() => { api.get("/voice/voices").then((r) => setVoices(r.data)).catch(() => setVoices([])); }, []);
  return voices || [];
}

export function SceneVoice({ scene, onChange, ensureSaved, voiceId }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const chunks = useRef([]);
  async function upload(blob) {
    setBusy(true);
    try {
      const id = await ensureSaved(); if (!id) return;
      const fd = new FormData(); fd.append("file", blob, "voice.webm");
      const { data } = await api.post(`/weddings/mine/invites/${id}/scene-voice`, fd);
      onChange(data.voice_path); toast.success("Voice added. Save the invite to keep it.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((t) => t.stop()); upload(new Blob(chunks.current, { type: mr.mimeType.split(";")[0] || "audio/webm" })); };
      mr.start(); setRec(mr);
      setTimeout(() => mr.state === "recording" && mr.stop(), 60000);
    } catch { toast.error("Allow microphone access to record"); }
  }
  function stop() { rec?.stop(); setRec(null); }
  async function ai() {
    if (!scene.text?.trim() && !scene.title?.trim()) return toast.error("Write the chapter text first");
    if (!voiceId) return toast.error("Voice isn't set up yet");
    setBusy(true);
    try {
      const id = await ensureSaved(); if (!id) return;
      const { data } = await api.post(`/weddings/mine/invites/${id}/scene-voice/ai`, { text: [scene.title, scene.text].filter(Boolean).join(". "), voice_id: voiceId });
      onChange(data.voice_path); toast.success("AI voice added. Save the invite to keep it.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  const btn = "flex items-center gap-1 text-[11px] rounded-full border border-[var(--line)] px-2 py-0.5 hover:border-[var(--gold)]";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="scene-voice">
      {busy ? <span className="text-[11px] text-[var(--muted)] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Adding voice…</span> : rec ? (
        <button onClick={stop} className={`${btn} !border-red-400 text-red-600`} data-testid="scene-voice-stop"><Square className="w-3 h-3" /> Stop recording</button>
      ) : (<>
        <button onClick={start} className={btn} data-testid="scene-voice-record"><Mic className="w-3 h-3" /> {scene.voice_path ? "Re-record" : "Record our voice"}</button>
        {voiceId ? <button onClick={ai} className={btn} data-testid="scene-voice-ai"><Wand2 className="w-3 h-3" /> AI voice</button>
          : <span className="text-[10px] text-[var(--muted)]" data-testid="scene-voice-ai-unavailable">AI voice is unavailable right now</span>}
      </>)}
      {scene.voice_path && !rec && !busy && <>
        <button onClick={() => new Audio(fileUrl(scene.voice_path)).play()} className={btn} data-testid="scene-voice-play"><Play className="w-3 h-3" /> Play</button>
        <button onClick={() => onChange(null)} className="text-[var(--muted)]" aria-label="Remove voice" data-testid="scene-voice-remove"><X className="w-3 h-3" /></button>
      </>}
    </div>
  );
}
